/**
 * Build-time surface gating.
 *
 * Kanäle (`integrations`) and Roadmap describe distribution channels and plans
 * that are not public yet, so for now they are internal-only: present when the
 * app is run from the dev server, absent from anything that is built and
 * served. This is a visibility decision, not a security boundary. The two views
 * are lazy chunks and their bytes still land in `dist/`; what the gate removes
 * is every way to *reach* them, which is what "internal only" means here.
 *
 * The switch is `import.meta.env.PROD`, deliberately, and not an APP_ENV build
 * arg. `.github/workflows/deploy.yml` builds ONE image and promotes it, so the
 * qa and prod artefacts are byte-identical by design; a flag that distinguished
 * them would mean shipping a prod image nobody had tested. `PROD` is true for
 * every `vite build`, so qa and prod agree and only the dev server differs.
 *
 * Vite replaces `import.meta.env.PROD` with a literal at build time, so in a
 * production bundle `isSurfaceEnabled` folds to a constant.
 *
 * Split pure-core / IO-shell like `scripts/license-rules.mjs` and
 * `scripts/check-license.mjs`: `surfaceEnabledIn` takes the mode as an argument
 * so both branches are testable without stubbing the build environment, and
 * `isSurfaceEnabled` is the thin reader that supplies it.
 */

/** Route kinds withheld from built bundles. Kept as strings rather than
 *  `RouteKind` so this module has no import edge back to `./useRoute`, which
 *  imports it. */
export const INTERNAL_ONLY_SURFACES: readonly string[] = Object.freeze([
  "integrations",
  "roadmap",
]);

export function isInternalOnlySurface(kind: string): boolean {
  return INTERNAL_ONLY_SURFACES.includes(kind);
}

/** Pure decision: may `kind` be reached in a build running in this mode? */
export function surfaceEnabledIn(isProduction: boolean, kind: string): boolean {
  return !isProduction || !isInternalOnlySurface(kind);
}

/** Whether `kind` may be reached in THIS build. */
export function isSurfaceEnabled(kind: string): boolean {
  return surfaceEnabledIn(import.meta.env.PROD === true, kind);
}
